import { React, useState, useEffect, useMemo } from 'react'
import { connect } from 'react-redux'
import UserSelectors from 'Stores/User/Selectors'
import AuthSelectors from 'Stores/Auth/Selectors'
import UserActions from 'Stores/User/Actions'
import styles from './OngoingSessionList.module.css'
import RequestState from 'Enums/RequestState'
import { IFText, IFTable } from 'Components'
import Moment from 'moment'
import { useTranslation } from 'react-i18next'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Skeleton } from '@mui/material'
import OngoingSessionDetails from 'Containers/OngoingSessionDetails/OngoingSessionDetails'
const Countries = require('world-countries/countries.json')

const OngoingSessionList = ({
  userId,
  ongoingSessions,
  fetchOngoingSessionsList,
  fetchOngoingSessionsListRequestState,
  fetchUserListRequestState,
  selectedUserId,
  selectedUserIndex,
  userDetailsRequestState,
  admin,
  tablesHeight,
}) => {
  const { t } = useTranslation()
  const [columnVisibility, setColumnVisibility] = useState({
    country: false,
  })
  const [viewCountry, setViewCountry] = useState(false)

  const [
    fetchOngoingSessionsListLoadingState,
    setFetchOngoingSessionsListLoadingState,
  ] = useState(true)
  useEffect(() => {
    if (selectedUserId) setFetchOngoingSessionsListLoadingState(true)
  }, [selectedUserId])
  useEffect(() => {
    if (fetchOngoingSessionsListRequestState === RequestState.SUCCEEDED)
      setFetchOngoingSessionsListLoadingState(false)
  }, [fetchOngoingSessionsListRequestState])

  useEffect(() => {
    setColumnVisibility({
      country: viewCountry,
    })
  }, [viewCountry])

  useEffect(() => {
    fetchOngoingSessionsList(userId)
  }, [userId])

  useEffect(() => {
    if (admin.registeredCountries.length > 1) {
      setViewCountry(true)
    } else {
      setViewCountry(false)
    }
  }, [admin])

  const ongoingSessionsTableColumns = useMemo(
    () => [
      {
        accessorKey: 'StationName',
        size: 130,
        header:
          fetchUserListRequestState === RequestState.LOADING ||
          (fetchOngoingSessionsListRequestState === RequestState.LOADING &&
            fetchOngoingSessionsListLoadingState) ||
          selectedUserIndex === -1 ? (
            <Skeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
            />
          ) : (
            t('UserPage.ongoingSessionsTableColumns.StationName')
          ),
        Cell: ({ row }) => {
          return ongoingSessions &&
            row.index < ongoingSessions.length &&
            userDetailsRequestState === RequestState.SUCCEEDED ? (
            <div className={styles.ColumnTitle}>
              <ExpandMoreIcon
                className={styles.ExpandMoreIcon}
                style={{
                  transform: row.getIsExpanded()
                    ? 'rotate(0deg)'
                    : 'rotate(-90deg)',
                }}
              />
              <div className={styles.TransactionName}>
                <IFText>{row.original.stationInfo?.[0]?.name}</IFText>
              </div>
            </div>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },
      {
        accessorKey: 'tokenID',
        size: 130,
        header:
          fetchUserListRequestState === RequestState.LOADING ||
          (fetchOngoingSessionsListRequestState === RequestState.LOADING &&
            fetchOngoingSessionsListLoadingState) ||
          selectedUserIndex === -1 ? (
            <Skeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
            />
          ) : (
            t('UserPage.ongoingSessionsTableColumns.tokenID')
          ),
        Cell: ({ row }) => {
          return ongoingSessions &&
            row.index < ongoingSessions.length &&
            userDetailsRequestState === RequestState.SUCCEEDED ? (
            <IFText>{row.original.chargingToken.uid}</IFText>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },
      {
        accessorKey: 'energyConsumed',
        size: 130,
        header:
          fetchUserListRequestState === RequestState.LOADING ||
          (fetchOngoingSessionsListRequestState === RequestState.LOADING &&
            fetchOngoingSessionsListLoadingState) ||
          selectedUserIndex === -1 ? (
            <Skeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
            />
          ) : (
            t('UserPage.ongoingSessionsTableColumns.energyConsumed')
          ),
        Cell: ({ row }) => {
          return ongoingSessions &&
            row.index < ongoingSessions.length &&
            userDetailsRequestState === RequestState.SUCCEEDED ? (
            <IFText>{row.original.energyConsumption?.toFixed(3)} kWh</IFText>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },
      {
        accessorKey: 'duration',
        header:
          fetchUserListRequestState === RequestState.LOADING ||
          (fetchOngoingSessionsListRequestState === RequestState.LOADING &&
            fetchOngoingSessionsListLoadingState) ||
          selectedUserIndex === -1 ? (
            <Skeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
            />
          ) : (
            t('UserPage.ongoingSessionsTableColumns.duration')
          ),
        Cell: ({ row }) => {
          const durationHr = parseInt(
            Moment(row.original.updatedAt).diff(
              row.original.startedAt,
              'hours',
            ),
          )
          const durationMin = Moment.duration(
            Moment(row.original.updatedAt).diff(Moment(row.original.startedAt)),
          ).minutes()
          const durationSecs = Moment.duration(
            Moment(row.original.updatedAt).diff(Moment(row.original.startedAt)),
          ).seconds()

          return ongoingSessions &&
            row.index < ongoingSessions.length &&
            userDetailsRequestState === RequestState.SUCCEEDED ? (
            <IFText>
              {(durationHr === 0 || durationHr / 10 < 1 ? '0' : '') +
                durationHr +
                ':' +
                (durationMin === 0 || durationMin / 10 < 1 ? '0' : '') +
                durationMin +
                ':' +
                (durationSecs === 0 || durationSecs / 10 < 1 ? '0' : '') +
                durationSecs}
            </IFText>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },
      {
        accessorKey: 'country',
        header:
          fetchUserListRequestState === RequestState.LOADING ||
          (fetchOngoingSessionsListRequestState === RequestState.LOADING &&
            fetchOngoingSessionsListLoadingState) ||
          selectedUserIndex === -1 ? (
            <Skeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
            />
          ) : (
            t('UserPage.ongoingSessionsTableColumns.country')
          ),
        Cell: ({ row }) => {
          let country = Countries.find(
            (country) =>
              country.cca2 === row.original.stationInfo?.[0]?.countryCode,
          )
          return ongoingSessions &&
            row.index < ongoingSessions.length &&
            userDetailsRequestState === RequestState.SUCCEEDED ? (
            <IFText>{country?.name.common}</IFText>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },
      {
        accessorKey: 'status',
        header:
          fetchUserListRequestState === RequestState.LOADING ||
          (fetchOngoingSessionsListRequestState === RequestState.LOADING &&
            fetchOngoingSessionsListLoadingState) ||
          selectedUserIndex === -1 ? (
            <Skeleton
              variant="text"
              width="110px"
              animation="none"
              height={20}
            />
          ) : (
            t('UserPage.ongoingSessionsTableColumns.status')
          ),
        Cell: ({ row }) => {
          return ongoingSessions &&
            row.index < ongoingSessions.length &&
            userDetailsRequestState === RequestState.SUCCEEDED ? (
            <IFText>{row.original.status}</IFText>
          ) : (
            <Skeleton variant="text" width="60%" animation="none" height={20} />
          )
        },
      },
    ],
    [
      ongoingSessions,
      fetchOngoingSessionsListRequestState,
      fetchUserListRequestState,
      fetchOngoingSessionsListLoadingState,
    ],
  )
  const renderDetailPanel = ({ row }) => <OngoingSessionDetails row={row} />

  return (
    <div className={styles.tables}>
      <IFTable
        columns={ongoingSessionsTableColumns}
        data={ongoingSessions}
        showSkeleton={
          fetchOngoingSessionsListRequestState === RequestState.LOADING ||
          (fetchOngoingSessionsListRequestState !== RequestState.SUCCEEDED &&
            ongoingSessions?.length === 0) ||
          selectedUserIndex === -1
        }
        tableMaxHeight={tablesHeight}
        tableSetHeight={'100%'}
        renderDetailPanel={renderDetailPanel}
        tableRequestState={fetchOngoingSessionsListRequestState}
        hideColumnQuery={columnVisibility}
      />
    </div>
  )
}

function mapDispatchToProps(dispatch) {
  return {
    fetchOngoingSessionsList: (userId) =>
      dispatch(UserActions.fetchOngoingSessionsList(userId)),
  }
}

const mapStateToProps = (state) => ({
  selectedUserId: UserSelectors.getSelectedUserId(state),
  selectedUserIndex: UserSelectors.getSelectedUserIndex(state),
  userDetailsRequestState: UserSelectors.getUserDetailsRequestState(state),
  ongoingSessions: UserSelectors.getOngoingSessions(state),
  fetchOngoingSessionsListRequestState:
    UserSelectors.getFetchOngoingSessionsListRequestState(state),
  fetchUserListRequestState: UserSelectors.getFetchUserListRequestState(state),
  admin: AuthSelectors.getUser(state),
})

export default connect(mapStateToProps, mapDispatchToProps)(OngoingSessionList)
